import React, { useEffect, useRef } from 'react';
import '../../../components/ConfirmModal/styles.css';
import '../styles/ResizeCanvasModal.css';

const ResizeCanvasModal = ({
  isOpen,
  loading,
  canSubmit,
  widthValue,
  heightValue,
  unitValue,
  currentWidth,
  currentHeight,
  errors,
  scaleContent,
  onWidthChange,
  onHeightChange,
  onUnitChange,
  onToggleScale,
  onBlurWidth,
  onBlurHeight,
  onCancel,
  onConfirm,
}) => {
  const modalRef = useRef(null);
  const widthRef = useRef(null);

  useEffect(() => {
    if (isOpen && widthRef.current) {
      widthRef.current.focus();
      widthRef.current.select();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!loading && onCancel) {
      onCancel();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && !loading) {
      event.preventDefault();
      onCancel();
      return;
    }

    if (event.key === 'Enter' && canSubmit && !loading) {
      event.preventDefault();
      onConfirm();
      return;
    }

    if (event.key !== 'Tab' || !modalRef.current) {
      return;
    }

    const focusable = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const focusableElements = Array.from(focusable).filter(
      (el) => !el.disabled,
    );
    if (focusableElements.length === 0) {
      return;
    }
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="confirm-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="confirm-modal-container resize-canvas-modal"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="resize-canvas-title"
      >
        <div className="confirm-modal-content">
          <h2 className="confirm-modal-title" id="resize-canvas-title">
            Resize canvas
          </h2>
          <p className="confirm-modal-message">
            Current size: {currentWidth}×{currentHeight} px
          </p>
          <div className="resize-canvas-fields">
            <label className="resize-canvas-field">
              <span>Width</span>
              <input
                ref={widthRef}
                type="number"
                min="100"
                max="8000"
                step="1"
                value={widthValue}
                onChange={(event) => onWidthChange(event.target.value)}
                onBlur={(event) => onBlurWidth(event.target.value)}
                disabled={loading}
                className="resize-canvas-input"
              />
              {errors?.width && (
                <span className="resize-canvas-error">{errors.width}</span>
              )}
            </label>
            <label className="resize-canvas-field">
              <span>Height</span>
              <input
                type="number"
                min="100"
                max="8000"
                step="1"
                value={heightValue}
                onChange={(event) => onHeightChange(event.target.value)}
                onBlur={(event) => onBlurHeight(event.target.value)}
                disabled={loading}
                className="resize-canvas-input"
              />
              {errors?.height && (
                <span className="resize-canvas-error">{errors.height}</span>
              )}
            </label>
            <label className="resize-canvas-field">
              <span>Unit</span>
              <select
                value={unitValue}
                onChange={(event) => onUnitChange(event.target.value)}
                disabled={loading}
                className="resize-canvas-select"
              >
                <option value="px">px</option>
              </select>
            </label>
          </div>

          <label className="resize-canvas-checkbox">
            <input
              type="checkbox"
              checked={scaleContent}
              onChange={onToggleScale}
              disabled={loading}
            />
            <span>Scale content</span>
          </label>
          <p className="resize-canvas-note">
            This changes the design size. You can undo later (undo coming soon).
          </p>

          <div className="confirm-modal-buttons resize-canvas-actions">
            <button
              type="button"
              className="confirm-modal-btn confirm-btn"
              onClick={onConfirm}
              disabled={!canSubmit || loading}
            >
              {loading ? 'Applying...' : 'Apply'}
            </button>
            <button
              type="button"
              className="confirm-modal-btn cancel-btn"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResizeCanvasModal;
